import { CalendarFilled } from '@ant-design/icons'
import { DatePicker, Input, Select, message } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { requestEmploymentContract } from '../../../../api/aopsHR.api'
import AopsCommonModalLayout from '../../../../components/apos/modal/AopsCommonModalLayout'
import '../orgChart.scss'
import { workTypeMap } from '../utils/employmentMappings'
const { TextArea } = Input

// 근로 계약서 모달
const LaborContractModal = ({
  open,
  onClose,
  personalInfo,
  employmentInfo,
  onRequest,
}) => {
  // 템플릿 기본값
  const TEMPLATE_DEFAULTS = {
    workplace: '서울시 강남구 테헤란로 501, 5층 하우투약 본사',
    workStartTime: '09:30',
    workEndTime: '18:30',
    breakTime: '12:30-13:30',
    workdays: '매주 5일(또는 매일단위)근무',
    weeklyHoliday: '주휴일 매주 일요일',
    paymentMethod: '근로자 명의 예금 통장에 입금',
    paymentDate: '매월 5일 (휴일의 경우 전일 지급)',
  }

  // 계약 유형 옵션
  const workTypeOptions = [
    { label: '계약직', value: '계약직' },
    { label: '정규직', value: '정규직' },
    { label: '정규직 전환용', value: '정규직 전환용' },
  ]

  // 기타급여 유무 옵션
  const hasOtherAllowanceOptions = [
    { label: '있음', value: 'true' },
    { label: '없음', value: 'false' },
  ]

  // 지급 방법 옵션
  const paymentMethodOptions = [
    {
      label: '근로자 명의 예금 통장에 입금',
      value: '근로자 명의 예금 통장에 입금',
    },
    { label: '현금 지급', value: '현금 지급' },
    { label: '기타', value: '기타' },
  ]

  const [formData, setFormData] = useState({
    workType: '',
    jobDescription: '',
    contractStartDate: null,
    contractEndDate: null,
    workStartTime: '',
    workEndTime: '',
    breakTime: '',
    totalSalary: '',
    hasOtherAllowance: '',
    otherAllowanceAmount: '',
    paymentDate: '',
    paymentMethod: '',
    workplace: '',
    workdays: '',
    weeklyHoliday: '',
  })

  const [isProcessing, setIsProcessing] = useState(false)

  // 모달이 열릴 때 personalInfo와 employmentInfo를 기반으로 초기값 설정
  useEffect(() => {
    if (open && (personalInfo || employmentInfo)) {
      // workType 매핑 (FULL_TIME -> 정규직, CONTRACT -> 계약직)
      const mappedWorkType =
        workTypeMap[employmentInfo?.workType] || employmentInfo?.workType || ''

      // joinDate를 계약 시작일로 사용
      const contractStartDate = employmentInfo?.joinDate
        ? dayjs(employmentInfo.joinDate)
        : null

      setFormData({
        workType: mappedWorkType || '',
        jobDescription: employmentInfo?.position || '',
        contractStartDate: contractStartDate,
        contractEndDate: null, // 종료일은 사용자가 입력
        workStartTime: TEMPLATE_DEFAULTS.workStartTime,
        workEndTime: TEMPLATE_DEFAULTS.workEndTime,
        breakTime: TEMPLATE_DEFAULTS.breakTime,
        totalSalary: '',
        hasOtherAllowance: 'true',
        otherAllowanceAmount: '200,000',
        paymentDate: TEMPLATE_DEFAULTS.paymentDate,
        paymentMethod: TEMPLATE_DEFAULTS.paymentMethod,
        workplace: TEMPLATE_DEFAULTS.workplace,
        workdays: TEMPLATE_DEFAULTS.workdays,
        weeklyHoliday: TEMPLATE_DEFAULTS.weeklyHoliday,
      })
    }
  }, [open, personalInfo, employmentInfo])

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  // 숫자 포맷팅 함수 (천 단위 구분자 추가, 최대 10억 제한)
  const formatNumber = (value) => {
    // 숫자가 아닌 문자 제거
    const numbersOnly = value.replace(/[^\d]/g, '')
    if (!numbersOnly) return ''
    // 숫자로 변환
    const numValue = parseInt(numbersOnly, 10)
    // 최대값 999,999,999 (10억 미만)로 제한
    const maxValue = 999999999
    const limitedValue = numValue > maxValue ? maxValue : numValue
    // 천 단위 구분자 추가
    return limitedValue.toLocaleString('ko-KR')
  }

  // 월급/연봉 변경 핸들러
  const handleSalaryChange = (e) => {
    const formattedValue = formatNumber(e.target.value)
    handleInputChange('totalSalary', formattedValue)
  }

  // 기타급여 금액 변경 핸들러
  const handleOtherAllowanceChange = (e) => {
    const formattedValue = formatNumber(e.target.value)
    handleInputChange('otherAllowanceAmount', formattedValue)
  }

  // 필수 필드 검증 함수 (API에 전송되는 필드만 검증)
  const validateForm = () => {
    const errors = []

    // 업무 내용 (API 전송 필드)
    if (!formData.jobDescription || formData.jobDescription.trim() === '') {
      errors.push('업무 내용을 입력해주세요.')
    }

    // 계약 시작일 (API 전송 필드)
    if (!formData.contractStartDate) {
      errors.push('계약 시작일을 입력해주세요.')
    }

    // 계약 종료일 (정규직이 아닌 경우에만 필수, API 전송 필드)
    if (formData.workType !== '정규직' && !formData.contractEndDate) {
      errors.push('계약 종료일을 입력해주세요.')
    }

    // 월급/연봉 (API 전송 필드)
    if (!formData.totalSalary || formData.totalSalary.trim() === '') {
      errors.push('월급/연봉을 입력해주세요.')
    }

    return errors
  }

  const isFormValid = () => validateForm().length === 0

  const handleRequest = async () => {
    // 필수 필드 검증
    const validationErrors = validateForm()
    if (validationErrors.length > 0) {
      message.error(validationErrors[0])
      return
    }

    setIsProcessing(true)
    try {
      // 서버로 전송할 데이터 매핑 (CreateContractRequest DTO 구조에 맞춤)
      const params = {
        employeeId: personalInfo?.employeeId || null,
        contractType: 'EMPLOYMENT', // 근로계약서는 EMPLOYMENT
        contractStartDate: formData.contractStartDate
          ? dayjs(formData.contractStartDate).format('YYYY-MM-DD')
          : null,
        contractEndDate: formData.contractEndDate
          ? dayjs(formData.contractEndDate).format('YYYY-MM-DD')
          : null,
        jobDescription: formData.jobDescription || '',
        salaryAmount: formData.totalSalary
          ? parseFloat(formData.totalSalary.replace(/,/g, ''))
          : null,
      }

      const userId = personalInfo?.userId || null

      if (!userId) {
        message.error('사용자 정보를 찾을 수 없습니다.')
        setIsProcessing(false)
        return
      }

      console.log('params::', params)

      const res = await requestEmploymentContract(params, { userId })
      console.log('requestEmploymentContract res::', res)
      if (res.status === 200) {
        message.success('서명 요청이 완료되었습니다.')
        onRequest?.(params)
        onClose()
      } else {
        message.error('서명 요청 중 오류가 발생했습니다.')
      }
    } catch (error) {
      console.error('서명 요청 오류:', error)
      message.error(
        error.response?.data?.message || '서명 요청 중 오류가 발생했습니다.',
      )
    } finally {
      setIsProcessing(false)
    }
  }

  const requesterName = personalInfo?.name || '-'

  return (
    <AopsCommonModalLayout
      open={open}
      onClose={onClose}
      title="근로계약서 서명하기"
    >
      <div className="form-container">
        <div className="form-wrapper">
          {/* 요청 진행 중 정보 */}
          <div className="contract-form-header">
            <h3 className="contract-form-subtitle">표준 근로 계약서</h3>
            <div className="contract-form-requester">
              요청 진행 중 : {requesterName}
            </div>
          </div>

          <div className="contract-form-table-wrapper">
            {/* 좌측 컬럼 */}
            <div className="contract-form-column">
              {/* 계약 유형 */}
              <div className="contract-form-row">
                <div className="contract-form-category-label">계약 유형</div>
                <div className="contract-form-field-cell">
                  <label className="contract-form-label">유형</label>
                  <Select
                    className="form-select"
                    placeholder="유형을 선택해주세요"
                    value={formData.workType || undefined}
                    onChange={(value) => handleInputChange('workType', value)}
                    options={workTypeOptions}
                    disabled
                  />
                </div>
              </div>

              {/* 업무 내용 */}
              <div className="contract-form-row">
                <div className="contract-form-category-label">업무 내용</div>
                <div className="contract-form-field-cell">
                  <label className="contract-form-label">내용</label>
                  <Input
                    placeholder="내용을 입력해주세요"
                    value={formData.jobDescription}
                    onChange={(e) =>
                      handleInputChange('jobDescription', e.target.value)
                    }
                    style={{ border: 'none' }}
                  />
                </div>
              </div>

              {/* 근로 계약 기간 */}
              <div className="contract-form-row contract-form-row-multi">
                <div className="contract-form-category-label">
                  근로 계약 기간
                </div>
                <div className="contract-form-field-cells">
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">계약 시작일</label>
                    <DatePicker
                      placeholder="시작일을 입력해주세요"
                      value={formData.contractStartDate}
                      onChange={(date) =>
                        handleInputChange('contractStartDate', date)
                      }
                      suffixIcon={<CalendarFilled />}
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">계약 종료일</label>
                    <DatePicker
                      placeholder="종료일을 입력해주세요"
                      value={formData.contractEndDate}
                      onChange={(date) =>
                        handleInputChange('contractEndDate', date)
                      }
                      suffixIcon={<CalendarFilled />}
                      disabled={formData.workType === '정규직'}
                    />
                  </div>
                </div>
              </div>

              {/* 소정 근로시간 */}
              <div className="contract-form-row contract-form-row-multi">
                <div className="contract-form-category-label">
                  소정 근로시간
                </div>
                <div className="contract-form-field-cells">
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">시작 시각</label>
                    <Input
                      placeholder="시간을 숫자로 입력해주세요"
                      value={formData.workStartTime}
                      onChange={(e) =>
                        handleInputChange('workStartTime', e.target.value)
                      }
                      readOnly
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">종료 시각</label>
                    <Input
                      placeholder="시각을 숫자로 입력해주세요"
                      value={formData.workEndTime}
                      onChange={(e) =>
                        handleInputChange('workEndTime', e.target.value)
                      }
                      readOnly
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">휴게 시간</label>
                    <Input
                      placeholder="시각을 숫자로 입력해주세요"
                      value={formData.breakTime}
                      onChange={(e) =>
                        handleInputChange('breakTime', e.target.value)
                      }
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* 월급/연봉 */}
              <div className="contract-form-row">
                <div className="contract-form-category-label">월급/연봉</div>
                <div className="contract-form-field-cell">
                  <label className="contract-form-label">월급/연봉</label>
                  <Input
                    placeholder="숫자만 입력해주세요"
                    value={formData.totalSalary}
                    onChange={handleSalaryChange}
                  />
                </div>
              </div>

              {/* 기타급여 유무 */}
              <div className="contract-form-row contract-form-row-multi">
                <div className="contract-form-category-label">
                  기타급여 유무
                </div>
                <div className="contract-form-field-cells">
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">기타급여 유무</label>
                    <Select
                      className="form-select"
                      placeholder="유/무를 선택해주세요"
                      value={formData.hasOtherAllowance || undefined}
                      onChange={(value) => {
                        handleInputChange('hasOtherAllowance', value)
                        // '없음' 선택 시 금액 초기화
                        if (value === 'false') {
                          handleInputChange('otherAllowanceAmount', '')
                        }
                      }}
                      options={hasOtherAllowanceOptions}
                      disabled
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">기타급여 금액</label>
                    <Input
                      placeholder="금액을 입력해주세요"
                      value={formData.otherAllowanceAmount}
                      onChange={handleOtherAllowanceChange}
                      disabled
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">임금 지급일</label>
                    <Input
                      placeholder="임금 지급일을 입력해주세요"
                      value={formData.paymentDate}
                      onChange={(e) =>
                        handleInputChange('paymentDate', e.target.value)
                      }
                      readOnly
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">지급 방법</label>
                    <Select
                      className="form-select"
                      placeholder="방법을 선택해주세요"
                      value={formData.paymentMethod || undefined}
                      onChange={(value) =>
                        handleInputChange('paymentMethod', value)
                      }
                      options={paymentMethodOptions}
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 우측 컬럼 */}
            <div className="contract-form-column">
              {/* 근무 장소 */}
              <div className="contract-form-row">
                <div className="contract-form-category-label">근무 장소</div>
                <div className="contract-form-field-cell">
                  <label className="contract-form-label">주소</label>
                  <TextArea
                    placeholder="주소를 입력해주세요"
                    value={formData.workplace}
                    onChange={(e) =>
                      handleInputChange('workplace', e.target.value)
                    }
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    style={{ resize: 'none' }}
                    readOnly
                  />
                </div>
              </div>

              {/* 근무일/휴일 */}
              <div className="contract-form-row contract-form-row-multi">
                <div className="contract-form-category-label">근무일/휴일</div>
                <div className="contract-form-field-cells">
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">근무요일</label>
                    <Input
                      placeholder="근무요일을 입력해주세요"
                      value={formData.workdays}
                      onChange={(e) =>
                        handleInputChange('workdays', e.target.value)
                      }
                      readOnly
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">주휴일</label>
                    <Input
                      placeholder="주휴일을 입력해주세요"
                      value={formData.weeklyHoliday}
                      onChange={(e) =>
                        handleInputChange('weeklyHoliday', e.target.value)
                      }
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* 연차 유급휴가 */}
              <div className="contract-form-row">
                <div className="contract-form-category-label">
                  연차 유급휴가
                </div>
                <div className="contract-form-static-text">
                  연차유급휴가는 근로기준법에서 정하는 바에 따라 부여함
                </div>
              </div>

              {/* 사회보험 적용 여부 */}
              <div className="contract-form-row">
                <div className="contract-form-category-label">
                  사회보험 적용 여부
                </div>
                <div className="contract-form-static-text">
                  고용보험, 산재보험, 국민연금, 건강보험
                </div>
              </div>

              {/* 근로계약서 교부 */}
              <div className="contract-form-row">
                <div className="contract-form-category-label">
                  근로계약서 교부
                </div>
                <div
                  className="contract-form-static-text"
                  style={{
                    fontSize: '12px',
                    color: '#191A1C',
                    letterSpacing: '-0.56px',
                    lineHeight: '1.5',
                  }}
                >
                  사업주는 근로계약을 체결함과 동시에 본 계약서를 사본하여
                  근로자의 교부요구와 관계없이 근로자에게 교부함(근로기준법 제
                  17조 이행)
                </div>
              </div>
            </div>
          </div>

          {/* 하단 버튼 */}
          <div className="popup-footer">
            <button className="btn-cancel" onClick={onClose}>
              취소
            </button>
            <button
              className="btn-submit"
              onClick={handleRequest}
              disabled={isProcessing || !isFormValid()}
            >
              {isProcessing ? '요청 중...' : '서명 요청하기'}
            </button>
          </div>
        </div>
      </div>
    </AopsCommonModalLayout>
  )
}

export default LaborContractModal
